import { Link } from "@tanstack/react-router";
import { Plus, Check } from "lucide-react";
import { useInquiry } from "@/lib/inquiry-store";
import { toast } from "sonner";
import type { Product } from "@/lib/catalog.types";
import { resolveImage, productImageSrcSet } from "@/lib/product-images";
import { useCategoryLabel } from "@/lib/use-catalog";

export function ProductCard({ product }: { product: Product }) {
  const { add, has } = useInquiry();
  const added = has(product.slug);
  const categoryLabel = useCategoryLabel(product.category_slug);
  const imgSrc = resolveImage(product.image);
  const { src, srcSet, sizes } = productImageSrcSet(imgSrc);

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (added) return;
    add({ slug: product.slug, name: product.name, image: imgSrc });
    toast.success(`${product.name} tillagd i din förfrågan`);
  };

  return (
    <Link
      to="/produkter/$slug"
      params={{ slug: product.slug }}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-white">
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={product.name}
          loading="lazy"
          decoding="async"
          width={800}
          height={600}
          className="h-full w-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
        />

        {product.brandable && (
          <span className="absolute left-2 top-2 sm:left-3 sm:top-3 rounded-full bg-accent text-accent-foreground text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-1 shadow-md">
            Går att branda
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {categoryLabel}
        </span>
        <h2 className="mt-2 font-display text-lg font-bold leading-tight">
          {product.name}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">
          {product.short}
        </p>
        <button
          type="button"
          onClick={handleAdd}
          className={`mt-4 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
            added
              ? "bg-primary text-primary-foreground"
              : "bg-accent text-accent-foreground hover:bg-accent/90"
          }`}
        >
          {added ? <><Check className="h-4 w-4" /> Tillagd</> : <><Plus className="h-4 w-4" /> Lägg i förfrågan</>}
        </button>
      </div>
    </Link>
  );
}
